/**
 * FUEL Calculator - Core Calculation Engine
 * Based on ISSN Position Stand (2017) + 2024/2025 Sports Nutrition Research
 *
 * Product: Gummy Gainz Strawberry Lemonade
 * - 20g protein per pouch
 * - 78g carbs per pouch
 * - 3.7:1 carb:protein ratio
 * - Heat stable to 140°F
 *
 * Idempotency: this script gets loaded by both fuel-calculator-section and
 * fuel-calculator-modal; the guard prevents a "FuelCalculator already declared"
 * SyntaxError when both render on the same page.
 */

if (typeof window.FuelCalculator !== 'undefined') {
  // already loaded — skip re-declaration
} else {

const FuelCalculator = (function() {
  'use strict';

  const PRODUCT = {
    name: 'Strawberry Lemonade',
    proteinPerPouch: 20,
    carbsPerPouch: 78,
    ratio: 3.7,
    proteinPerServing: 7,
    carbsPerServing: 26,
    servingsPerPouch: 3,
    heatStable: 140,
    shopUrl: 'https://gummygainz.co/products/gummy-gainz-protein-candy'
  };

  const SPORT_FACTORS = {
    endurance: { key: 'endurance', label: 'Endurance', emoji: '🏃', carbBase: 1.2, dailyRange: [7, 13], proteinMod: 1.0, description: 'Endurance athletes have the highest glycogen demands', examples: 'running, cycling, triathlon, swimming' },
    strength: { key: 'strength', label: 'Strength/Power', emoji: '🏋️', carbBase: 0.8, dailyRange: [4, 7], proteinMod: 1.15, description: 'Strength athletes need moderate carbs for explosive power', examples: 'weightlifting, powerlifting, CrossFit' },
    team: { key: 'team', label: 'Team Sports', emoji: '⚽', carbBase: 1.0, dailyRange: [6, 10], proteinMod: 1.0, description: 'Team sport athletes face intermittent high-intensity demands', examples: 'soccer, basketball, volleyball, hockey' },
    combat: { key: 'combat', label: 'Combat Sports', emoji: '🥊', carbBase: 0.9, dailyRange: [4, 7], proteinMod: 1.1, description: 'Combat athletes balance power and endurance needs', examples: 'wrestling, BJJ, MMA, boxing' },
    hybrid: { key: 'hybrid', label: 'Hybrid/Functional', emoji: '🔥', carbBase: 1.0, dailyRange: [5, 8], proteinMod: 1.05, description: 'Hybrid athletes need versatile fueling strategies', examples: 'Hyrox, OCR, general training' }
  };

  const DURATION_FACTORS = {
    'under-45': { key: 'under-45', label: 'Under 45 minutes', emoji: '⚡', multiplier: 0.7, depletion: '30-50%' },
    '45-75': { key: '45-75', label: '45-75 minutes', emoji: '🕐', multiplier: 1.0, depletion: '50-70%' },
    '75-120': { key: '75-120', label: '75-120 minutes', emoji: '🏆', multiplier: 1.2, depletion: '70-90%' },
    '2plus': { key: '2plus', label: '2+ hours', emoji: '🔥', multiplier: 1.5, depletion: 'Near total' }
  };

  const AGE_FACTORS = {
    youth: { key: 'youth', label: 'Youth Athlete (under 18)', emoji: '👦', proteinMultiplier: 1.1, carbMultiplier: 0.9, urgencyBoost: true, minProtein: 20, note: 'Youth athletes deplete glycogen faster - timing is critical.' },
    adult: { key: 'adult', label: 'Adult Athlete (18-35)', emoji: '🧑', proteinMultiplier: 1.0, carbMultiplier: 1.0, urgencyBoost: false, minProtein: 20, note: null },
    masters: { key: 'masters', label: 'Masters Athlete (35+)', emoji: '👨', proteinMultiplier: 1.15, carbMultiplier: 1.0, urgencyBoost: false, minProtein: 25, note: 'Masters athletes benefit from higher protein intake.' }
  };

  const TRAINING_STATE_FACTORS = {
    fasted: { key: 'fasted', label: 'Fasted (empty stomach)', emoji: '🌅', window: [20, 60], urgency: 'WINDOW: OPEN', urgencyColor: '#E6624E', description: 'Empty tank. This is your target for the first 60 minutes after training.' },
    fed: { key: 'fed', label: 'Fed (2-3 hours after meal)', emoji: '🍽️', window: [60, 180], urgency: 'WINDOW: WIDE', urgencyColor: '#1C1C1E', description: 'Pre-workout meal is still working. Land this target across the next 1–3 hours.' },
    partial: { key: 'partial', label: 'Partially fed (light snack)', emoji: '🍌', window: [30, 90], urgency: 'WINDOW: ACTIVE', urgencyColor: '#E6624E', description: 'Light fuel onboard. Hit this target across the next 60–90 minutes.' }
  };

  const SCHEDULE_FACTORS = {
    single: { key: 'single', label: 'Single Event', emoji: '🎯', carbMultiplier: 1.0, urgencyBoost: false, note: 'Standard recovery protocol' },
    tournament: { key: 'tournament', label: 'Tournament Day', emoji: '🏆', carbMultiplier: 1.3, urgencyBoost: true, note: 'TOURNAMENT MODE: Maximize glycogen for next game' },
    training: { key: 'training', label: 'Training Block', emoji: '📅', carbMultiplier: 0.9, urgencyBoost: false, note: 'Focus on adaptation, not just recovery' },
    twoaday: { key: 'twoaday', label: 'Two-a-Days', emoji: '🔄', carbMultiplier: 1.2, urgencyBoost: true, note: 'Rapid recovery essential for second session' }
  };

  const FREQUENCY_OPTIONS = {
    '3-4': { key: '3-4', label: '3-4 sessions (recreational)', avgSessions: 3.5, tier: 'starter' },
    '5-6': { key: '5-6', label: '5-6 sessions (dedicated)', avgSessions: 5.5, tier: 'performance' },
    '7+': { key: '7+', label: '7+ sessions (competitive)', avgSessions: 8, tier: 'elite' }
  };

  function calculate(inputs) {
    if (!validateInputs(inputs)) return { error: 'Invalid inputs provided' };

    const weightKg = inputs.unit === 'lbs' ? inputs.weight / 2.205 : inputs.weight;
    const sportFactor = SPORT_FACTORS[inputs.sport];
    const durationFactor = DURATION_FACTORS[inputs.duration];
    const ageFactor = AGE_FACTORS[inputs.age];
    const trainingStateFactor = TRAINING_STATE_FACTORS[inputs.trainingState];
    const scheduleFactor = SCHEDULE_FACTORS[inputs.schedule];
    const frequencyOption = FREQUENCY_OPTIONS[inputs.frequency];

    let carbsNeeded = weightKg * sportFactor.carbBase * durationFactor.multiplier * ageFactor.carbMultiplier * scheduleFactor.carbMultiplier;
    let proteinNeeded = Math.max(weightKg * 0.3 * ageFactor.proteinMultiplier * sportFactor.proteinMod, ageFactor.minProtein);
    proteinNeeded = Math.min(proteinNeeded, 40);

    const calculatedRatio = carbsNeeded / proteinNeeded;
    if (calculatedRatio < 3 && carbsNeeded >= weightKg * 0.5) carbsNeeded = proteinNeeded * 3.5;

    carbsNeeded = Math.round(carbsNeeded);
    proteinNeeded = Math.round(proteinNeeded);
    const ratio = (carbsNeeded / proteinNeeded).toFixed(1);

    const baseWindow = trainingStateFactor.window;
    const isUrgent = trainingStateFactor.urgency === 'CRITICAL' || ageFactor.urgencyBoost || scheduleFactor.urgencyBoost;

    const timing = {
      min: isUrgent ? Math.min(baseWindow[0], 30) : baseWindow[0],
      max: isUrgent ? Math.min(baseWindow[1], 60) : baseWindow[1],
      urgency: isUrgent ? 'WINDOW: OPEN' : trainingStateFactor.urgency,
      urgencyColor: isUrgent ? '#E6624E' : trainingStateFactor.urgencyColor,
      label: trainingStateFactor.description
    };

    const pouchesForProtein = Math.ceil(proteinNeeded / PRODUCT.proteinPerPouch);
    const pouchesForCarbs = Math.ceil(carbsNeeded / PRODUCT.carbsPerPouch);
    const pouchesRecommended = Math.max(pouchesForProtein, pouchesForCarbs);

    const recommendation = {
      pouches: pouchesRecommended,
      servings: Math.ceil(proteinNeeded / PRODUCT.proteinPerServing),
      carbsDelivered: pouchesRecommended * PRODUCT.carbsPerPouch,
      proteinDelivered: pouchesRecommended * PRODUCT.proteinPerPouch,
      ratioDelivered: PRODUCT.ratio
    };

    const weeklyPouches = Math.ceil(frequencyOption.avgSessions * pouchesRecommended);
    const bundle = getBundleRecommendation(weeklyPouches, frequencyOption.tier);

    const scienceNotes = [sportFactor.description, durationFactor.label + ' = ' + durationFactor.depletion + ' glycogen depletion'];
    if (ageFactor.note) scienceNotes.push(ageFactor.note);
    scienceNotes.push(scheduleFactor.note);

    const tags = generateTags(inputs, timing, bundle);

    const comparison = {
      needed: { carbs: carbsNeeded, protein: proteinNeeded, ratio: ratio },
      delivered: { carbs: recommendation.carbsDelivered, protein: recommendation.proteinDelivered, ratio: PRODUCT.ratio }
    };

    return {
      success: true,
      inputs: { weight: inputs.weight, weightKg: Math.round(weightKg * 10) / 10, unit: inputs.unit, sport: sportFactor, duration: durationFactor, age: ageFactor, trainingState: trainingStateFactor, schedule: scheduleFactor, frequency: frequencyOption },
      results: { carbsNeeded, proteinNeeded, ratio, timing, recommendation, bundle, comparison, scienceNotes, tags },
      product: PRODUCT
    };
  }

  function validateInputs(inputs) {
    const required = ['weight', 'unit', 'sport', 'duration', 'age', 'trainingState', 'schedule', 'frequency'];
    for (const field of required) { if (!inputs[field]) return false; }
    if (inputs.unit === 'lbs') {
      if (inputs.weight < 66 || inputs.weight > 352) return false;
    } else {
      if (inputs.weight < 30 || inputs.weight > 160) return false;
    }
    if (!SPORT_FACTORS[inputs.sport]) return false;
    if (!DURATION_FACTORS[inputs.duration]) return false;
    if (!AGE_FACTORS[inputs.age]) return false;
    if (!TRAINING_STATE_FACTORS[inputs.trainingState]) return false;
    if (!SCHEDULE_FACTORS[inputs.schedule]) return false;
    if (!FREQUENCY_OPTIONS[inputs.frequency]) return false;
    return true;
  }

  function getBundleRecommendation(weeklyPouches, tier) {
    if (weeklyPouches <= 7 || tier === 'starter') return { name: 'Starter Pack', quantity: 7, subscription: false, savings: null, cta: 'Get Started', weeklyNeed: weeklyPouches };
    if (weeklyPouches <= 14 || tier === 'performance') return { name: 'Performance Pack', quantity: 14, subscription: true, savings: '15%', frequency: 'bi-weekly', cta: 'Subscribe & Save 15%', weeklyNeed: weeklyPouches };
    return { name: 'Elite Bundle', quantity: 21, subscription: true, savings: '20%', frequency: 'weekly', cta: 'Subscribe & Save 20%', weeklyNeed: weeklyPouches };
  }

  function generateTags(inputs, timing, bundle) {
    const tags = ['sport-' + inputs.sport, 'age-' + inputs.age, 'training-state-' + inputs.trainingState, 'schedule-' + inputs.schedule];
    if (inputs.age === 'youth') tags.push('youth-athlete');
    if (inputs.duration === '75-120' || inputs.duration === '2plus') tags.push('duration-long', 'high-volume-carbs');
    if (inputs.trainingState === 'fasted') tags.push('fasted-trainer');
    if (inputs.schedule === 'tournament') tags.push('tournament-athlete');
    if (inputs.schedule === 'twoaday') tags.push('elite-volume');
    if (inputs.frequency === '7+') tags.push('frequency-high', 'elite-frequency');
    if (timing.urgency === 'WINDOW: OPEN') tags.push('urgent-timing');
    tags.push(bundle.name.toLowerCase().replace(' ', '-') + '-fit', 'fuel-calculator-lead');
    return tags;
  }

  return { calculate, SPORT_FACTORS, DURATION_FACTORS, AGE_FACTORS, TRAINING_STATE_FACTORS, SCHEDULE_FACTORS, FREQUENCY_OPTIONS, PRODUCT };
})();

if (typeof module !== 'undefined' && module.exports) { module.exports = FuelCalculator; }

window.FuelCalculator = FuelCalculator;
} // end idempotency guard